import React, { useEffect, useState } from "react";
import facultyAPI from "../api/facultyApi";
import { showSuccess, showError } from "../utils/toastUtils";
import useAlphabeticInput from "../hooks/useAlphabeticInput";
import useNumericInput from "../hooks/useNumericInput";

const CareerForm = ({ showModal, activeJob, closeForm }) => {
  useAlphabeticInput();
  useNumericInput();

  const INITIAL_FORM_DATA = {
    full_name: "",
    email: "",
    contact: "",
    dob: "",
    professional: "",
    branch: activeJob || "",
    subject: "",
    experience: "",
    qualification: "",
    teachingmethod: "",
    resume: null,
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = () => {
    const newErrors = {};

    if (!formData.full_name) {
      newErrors.full_name = "Please enter full name";
    }

    if (!formData.email) {
      newErrors.email = "Please enter email address";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.contact) {
      newErrors.contact = "Please enter contact number";
    } else if (formData.contact.length !== 10) {
      newErrors.contact = "Contact must be 10 digits";
    }

    if (!formData.dob) {
      newErrors.dob = "Please enter date of birth";
    }

    if (!formData.professional) {
      newErrors.professional = "Please enter professional background";
    }

    if (!formData.position) {
      newErrors.position = "Please enter branch";
    }

    if (!formData.subject) {
      newErrors.subject = "Please enter subject";
    }

    if (!formData.experience) {
      newErrors.experience = "Please enter experience";
    }

    if (!formData.qualification) {
      newErrors.qualification = "Please enter qualification";
    }

    if (!formData.resume) {
      newErrors.resume = "Please upload resume";
    }

    if (!formData.teachingmethod) {
      newErrors.teachingmethod = "Please enter teaching methodology";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          data.append(key, value);
        }
      });

      const res = await facultyAPI.career_form(data);

      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITIAL_FORM_DATA);
        closeForm();
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
  setFormData((prev) => ({
    ...prev,
    position: activeJob || "",
  }));
}, [activeJob]);


  if (!showModal) return null;


  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.9)", zIndex: 1060 }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered px-3">
        <div className="modal-content mrv-career-form-modal-box">
          <div className="mrv-career-form-modal-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0 fw-bold text-white">Application Form</h5>
              <span className="small text-light opacity-75">
                Position: {activeJob}
              </span>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={closeForm}
            ></button>
          </div>

          <div className="mrv-career-form-modal-body">
            <form className="container-fluid px-0" onSubmit={handleSubmit}>
              <div className="row row-cols-1 row-cols-md-2 g-4">
                <div className="col text-start">
                  <label className="mrv-career-form-label">Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    className="mrv-career-form-input only-alpha"
                    placeholder="Name"
                    value={formData.full_name}
                    onChange={handleChange}
                  />
                  {errors.full_name && (
                    <small className="text-danger">{errors.full_name}</small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    name="contact"
                    className="mrv-career-form-input only-numeric"
                    placeholder="+91..."
                    value={formData.contact}
                    onChange={handleChange}
                    maxLength={10}
                  />
                  {errors.contact && (
                    <small className="text-danger">{errors.contact}</small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">Email ID</label>
                  <input
                    type="email"
                    name="email"
                    className="mrv-career-form-input"
                    placeholder="email@school.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    className="mrv-career-form-input"
                    value={formData.dob}
                    onChange={handleChange}
                  />
                  {errors.dob && (
                    <small className="text-danger">{errors.dob}</small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">
                    Total Experience
                  </label>
                  <input
                    type="text"
                    name="experience"
                    className="mrv-career-form-input"
                    placeholder="Years"
                    value={formData.experience}
                    onChange={handleChange}
                  />
                  {errors.experience && (
                    <small className="text-danger">{errors.experience}</small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">Qualification</label>
                  <input
                    type="text"
                    name="qualification"
                    className="mrv-career-form-input"
                    placeholder="e.g. B.Ed, M.A"
                    value={formData.qualification}
                    onChange={handleChange}
                  />
                  {errors.qualification && (
                    <small className="text-danger">
                      {errors.qualification}
                    </small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">Branch</label>
                  <input
                    type="text"
                    name="branch"
                    className="mrv-career-form-input"
                    placeholder="Branch"
                    value={formData.position}
                    onChange={handleChange}
                  />
                  {errors.position && (
                    <small className="text-danger">{errors.position}</small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    className="mrv-career-form-input"
                    placeholder="e.g. Mathematics"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                  {errors.subject && (
                    <small className="text-danger">{errors.subject}</small>
                  )}
                </div>

                <div className="col text-start">
                  <label className="mrv-career-form-label">
                    Professional Background
                  </label>
                  <input
                    type="text"
                    name="professional"
                    className="mrv-career-form-input"
                    placeholder="e.g. Teacher, Lecturer"
                    value={formData.professional}
                    onChange={handleChange}
                  />
                  {errors.professional && (
                    <small className="text-danger">{errors.professional}</small>
                  )}
                </div>
              </div>

              <div className="mt-4 text-start">
                <label className="mrv-career-form-label">Resume (PDF)</label>
                <input
                  type="file"
                  name="resume"
                  className="mrv-career-form-input"
                  accept=".pdf"
                  onChange={handleChange}
                />
                {errors.resume && (
                  <small className="text-danger">{errors.resume}</small>
                )}
              </div>

              <div className="mt-4 text-start">
                <label className="mrv-career-form-label">
                  Teaching Methodology
                </label>
                <textarea
                  name="teachingmethod"
                  className="mrv-career-form-input"
                  rows="3"
                  placeholder="Briefly explain your approach..."
                  value={formData.teachingmethod}
                  onChange={handleChange}
                ></textarea>
                {errors.teachingmethod && (
                  <small className="text-danger">{errors.teachingmethod}</small>
                )}
              </div>

              <div className="d-flex justify-content-center align-items-center mt-4">
                <button
                  type="submit"
                  className="mrv-career-form-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "SUBMIT APPLICATION"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerForm;
